import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { getInvoices, getInvoiceUsage } from '../redux/slices/invoiceSlice';
import UsageMeter from '../components/UsageMeter';
import OnboardingChecklist from '../components/OnboardingChecklist';
import { getMySubscription } from '../redux/slices/subscriptionSlice';
import { FileText, Plus, DollarSign, Clock, CheckCircle, Crown, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import axios from 'axios';

const Dashboard = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const { invoices, isLoading, usage } = useSelector((state) => state.invoice);
  const { user } = useSelector((state) => state.auth);
  const { subscription, isLoading: subscriptionLoading } = useSelector((state) => state.subscription);

  const API_URL = import.meta.env.VITE_API_URL || 'https://api-invoice.ainixit.com/api';

  useEffect(() => {
    dispatch(getInvoices());
    dispatch(getInvoiceUsage());
    dispatch(getMySubscription());
  }, [dispatch]);

  // Handle session completion when user returns from Stripe checkout
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && !isProcessingPayment) {
      handleSessionComplete(sessionId);
    }
  }, [searchParams, isProcessingPayment]);

  const handleSessionComplete = async (sessionId) => {
    setIsProcessingPayment(true);
    try {
      console.log('Processing Stripe session:', sessionId);
      
      const response = await axios.post(
        `${API_URL}/subscriptions/session-complete`,
        { sessionId },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        toast.success('Payment successful! Your subscription has been activated.');
        
        // Refresh subscription data
        dispatch(getMySubscription());
        
        // Remove session_id from URL
        searchParams.delete('session_id');
        setSearchParams(searchParams);
      } else {
        toast.error(response.data.message || 'Payment processing failed');
      }
    } catch (error) {
      console.error('Session completion error:', error);
      toast.error(error.response?.data?.message || 'Failed to process payment');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const stats = {
    total: invoices.length,
    draft: invoices.filter(inv => inv.status === 'draft').length,
    sent: invoices.filter(inv => inv.status === 'sent').length,
    paid: invoices.filter(inv => inv.status === 'paid').length,
    totalRevenue: invoices
      .filter(inv => inv.status === 'paid')
      .reduce((sum, inv) => sum + inv.total, 0),
  };

  const recentInvoices = invoices.slice(0, 5);

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'badge badge-info',
      sent: 'badge badge-warning',
      paid: 'badge badge-success',
      overdue: 'badge badge-danger',
      cancelled: 'badge bg-gray-100 text-gray-800',
    };
    return badges[status] || 'badge';
  };

  return (
    <div className="space-y-6">
      {/* Payment Processing Indicator */}
      {isProcessingPayment && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-600"></div>
            <div>
              <h3 className="text-sm font-medium text-blue-800">Processing Payment</h3>
              <p className="text-sm text-blue-700">Please wait while we activate your subscription...</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back, {user?.profile?.firstName || user?.email}</p>
        </div>
        {usage?.atCap ? (
          <Link to="/subscriptions" className="btn btn-primary">
            Upgrade
          </Link>
        ) : (
          <Link to="/invoices/new" className="btn btn-primary flex items-center space-x-2">
            <Plus className="w-5 h-5" />
            <span>Create Invoice</span>
          </Link>
        )}
      </div>

      <UsageMeter usage={usage} />

      <OnboardingChecklist user={user} invoices={invoices} />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Invoices</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Draft</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.draft}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Paid</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.paid}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                ${stats.totalRevenue.toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Status */}
      {subscriptionLoading ? (
        <div className="card">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      ) : subscription?.plan_id ? (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Crown className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {subscription.plan_id.name} Plan
                </h3>
                <p className="text-sm text-gray-600">
                  ${subscription.plan_id.price}/{subscription.plan_id.interval}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>
                  {subscription.current_period_end 
                    ? `Renews ${format(new Date(subscription.current_period_end), 'MMM dd, yyyy')}`
                    : 'Active'
                  }
                </span>
              </div>
              <div className="mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  subscription.status === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {subscription.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800">Free Plan</h3>
              <p className="text-sm text-yellow-700 mt-1">
                You're on the free plan with limited features. 
                <Link to="/subscriptions" className="font-medium underline ml-1">
                  Upgrade to unlock more features
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Recent Invoices */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Recent Invoices</h2>
          <Link to="/invoices" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
            View All
          </Link>
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : recentInvoices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Invoice #</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Client</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Amount</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Date</th>
                </tr>
              </thead>
              <tbody>
                {recentInvoices.map((invoice) => (
                  <tr key={invoice._id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <Link to={`/invoices/edit/${invoice._id}`} className="text-primary-600 hover:underline">
                        {invoice.invoiceNumber}
                      </Link>
                    </td>
                    <td className="py-3 px-4">{invoice.client_info.name}</td>
                    <td className="py-3 px-4 font-medium">
                      ${invoice.total.toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <span className={getStatusBadge(invoice.status)}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {format(new Date(invoice.issueDate), 'MMM dd, yyyy')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No invoices yet</p>
            <Link to="/invoices/new" className="text-primary-600 hover:text-primary-700 text-sm font-medium mt-2 inline-block">
              Create your first invoice
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;

